/**
 * AdPlacementWidget — T4 «Placement mix: игровое состояние и тип награды»
 * =========================================================================
 *
 * Спека: .claude/plans/2026-08-25-avz-ad-monetization-dashboards.md, T4.
 *
 * Три блока:
 *  - горизонтальный stacked bar доли выручки по игровому состоянию (p2str)
 *    по дням/неделям/месяцам, топ-7 + Other;
 *  - сортируемая таблица по 33 типам награды (p3str): пользователи,
 *    показов/пользователя, показов/сессию, выручка/пользователя, доля выручки, Δ WoW;
 *
 * Диапазон дат дашборда трактуется календарно (event_ts), не как когорта.
 * Подвал графика подписан долей событийной выручки от биллинговой
 * (prod_admon) — рассчитывается на сервере динамически.
 */

import React, { useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from '../BaseWidget';
import { useAdsWidgetData } from '../../../hooks/useAdsGeo';
import { AdsService } from '../../../services/adsService';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Tooltip } from '../../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../../ui/responsive-chart';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { adsSources as adsSourcesSpecs } from '@ggstat/chart-specs';

// последний — Other
const STATE_COLORS = [
  ...adsSourcesSpecs.placementStates.palette,
  adsSourcesSpecs.placementStates.overrides.Other
];

const GROUP_BY_OPTIONS = adsSourcesSpecs.placementGroupBy;

const fmtInt = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtUsd = (v) => `$${Number(v || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;

/** Разворачивает stateWeekly (плоский список period/state/revenue/share) в строки для stacked bar. */
function buildStatePeriods(stateWeekly) {
  const periods = new Map();
  const states = new Set();
  for (const row of stateWeekly || []) {
    states.add(row.state);
    if (!periods.has(row.period)) periods.set(row.period, { period: row.period });
    periods.get(row.period)[row.state] = row.share;
  }
  const stateList = [...states].sort((a, b) => (a === 'Other' ? 1 : b === 'Other' ? -1 : 0));
  return {
    rows: [...periods.values()].sort((a, b) => (a.period < b.period ? -1 : 1)),
    states: stateList
  };
}

function SortHeader({ label, active, dir, onClick }) {
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 hover:text-foreground text-muted-foreground"
    >
      {label}
      <Icon className="h-3 w-3" />
    </button>
  );
}

// Подписи колонок — из общей спеки (их же отдаёт MCP), форматтеры — здесь
const REWARD_COLUMN_FORMATS = {
  reward: { numeric: false },
  users: { numeric: true, fmt: fmtInt },
  impsPerUser: { numeric: true, fmt: (v) => v.toFixed(2) },
  impsPerSession: { numeric: true, fmt: (v) => v.toFixed(3) },
  revenuePerUser: { numeric: true, fmt: (v) => `$${v.toFixed(4)}` },
  revenueShare: { numeric: true, fmt: fmtPct },
  wowDeltaShare: { numeric: true, fmt: (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)} п.п.`) }
};
const REWARD_COLUMNS = adsSourcesSpecs.placementRewardColumns.map((col) => ({
  ...col,
  ...REWARD_COLUMN_FORMATS[col.key]
}));

export function AdPlacementWidget() {
  const [groupBy, setGroupBy] = useState('week');
  const [sortKey, setSortKey] = useState('revenueShare');
  const [sortDir, setSortDir] = useState('desc');

  const placementsData = useAdsWidgetData(
    async (dashboardFilters, options) => AdsService.get('/placements', dashboardFilters, { groupBy }, options),
    [groupBy]
  );

  const widgetProps = useBaseWidget(placementsData);
  const { data } = widgetProps;

  const { rows: statePeriods, states } = useMemo(
    () => buildStatePeriods(data?.stateWeekly),
    [data?.stateWeekly]
  );

  const sortedRewardTable = useMemo(() => {
    const rows = [...(data?.rewardTable || [])];
    rows.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return rows;
  }, [data?.rewardTable, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  return (
    <BaseWidget
      {...widgetProps}
      title="Placement mix: игровое состояние и тип награды"
      subtitle="Куда расширять/резать/переоценивать вознаграждённые показы"
      emptyMessage="Нет данных за выбранный диапазон"
      skeletonRows={10}
      headerAction={
        <div className="flex gap-1 text-xs">
          {GROUP_BY_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setGroupBy(value)}
              className={`px-2 py-1 rounded border ${groupBy === value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
            >
              {label}
            </button>
          ))}
        </div>
      }
    >
      <div className="space-y-6">
        {/* Доля выручки по игровому состоянию (p2str), по периодам */}
        <div>
          <p className="text-sm font-medium mb-2">Доля выручки по игровому состоянию, по периодам</p>
          {statePeriods.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(220, statePeriods.length * 32)}>
              <BarChart data={statePeriods} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis type="number" tickFormatter={(v) => `${Math.round(v * 100)}%`} domain={[0, 1]} className="text-xs" />
                <YAxis
                  type="category"
                  dataKey="period"
                  className="text-xs"
                  width={90}
                  tickFormatter={(p) => (groupBy === 'month' ? String(p).slice(0, 7) : p)}
                />
                <Tooltip order="series" formatter={(v) => fmtPct(v)} />
                <Legend />
                {states.map((state, idx) => (
                  <Bar
                    key={state}
                    dataKey={state}
                    stackId="revenue"
                    name={state}
                    fill={STATE_COLORS[state === 'Other' ? STATE_COLORS.length - 1 : idx % (STATE_COLORS.length - 1)]}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-6 text-sm text-muted-foreground">Нет данных</div>
          )}
          {data?.coverageNote && (
            <p className="text-xs text-muted-foreground mt-2">{data.coverageNote}</p>
          )}
        </div>

        {/* Таблица по 33 типам награды */}
        <div>
          <p className="text-sm font-medium mb-2">Типы награды (p3str)</p>
          <Table>
            <TableHeader>
              <TableRow>
                {REWARD_COLUMNS.map((col) => (
                  <TableHead key={col.key} className={col.numeric ? 'text-right' : ''}>
                    <SortHeader
                      label={col.label}
                      active={sortKey === col.key}
                      dir={sortDir}
                      onClick={() => handleSort(col.key)}
                    />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRewardTable.map((row) => (
                <TableRow key={row.reward}>
                  <TableCell className="font-medium">{row.reward}</TableCell>
                  <TableCell className="text-right">{fmtInt(row.users)}</TableCell>
                  <TableCell className="text-right">{row.impsPerUser.toFixed(2)}</TableCell>
                  <TableCell className="text-right">{row.impsPerSession.toFixed(3)}</TableCell>
                  <TableCell className="text-right">{fmtUsd(row.revenuePerUser)}</TableCell>
                  <TableCell className="text-right">{fmtPct(row.revenueShare)}</TableCell>
                  <TableCell className={`text-right ${row.wowDeltaShare == null ? 'text-muted-foreground' : row.wowDeltaShare >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {row.wowDeltaShare == null ? '—' : `${row.wowDeltaShare >= 0 ? '+' : ''}${(row.wowDeltaShare * 100).toFixed(1)} п.п.`}
                  </TableCell>
                </TableRow>
              ))}
              {sortedRewardTable.length === 0 && (
                <TableRow>
                  <TableCell colSpan={REWARD_COLUMNS.length} className="text-center text-sm text-muted-foreground py-6">
                    Нет данных
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </BaseWidget>
  );
}

export default AdPlacementWidget;
